/**
 * ProjectDetail-new.tsx
 * Redesigned Project Detail page.
 * Hosts all business logic and renders the redesigned
 * "Repository Test Generation" tab along with the other tabs.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import
{
    ChevronRight,
    FolderFill,
} from 'react-bootstrap-icons';

import ProjectRequirementsPanelNew from '../components/requirements/ProjectRequirementsPanel-new';
import RequirementTestWorkspace from '../components/requirements/RequirementTestWorkspace';
import DomainKnowledgePanel from '../components/domain/DomainKnowledgePanel';
import ProjectChatPanel from '../components/chat/ProjectChatPanel';
import ExecutionsTab from '../components/executions/ExecutionsTab';
import SyntheticDataTab from '../components/synthetic/SyntheticDataTab';
import GherkinTab from '../components/requirements/GherkinTab';
import MaintenancePage from '../components/MaintenancePage';
import ProjectAnalyticsTab from '../components/projects/ProjectAnalyticsTab';
import TestGenerations from './TestGenerations';
import { useProjects } from '../contexts/ProjectContext';
import { useActiveFeatureFlags } from '../hooks/useActiveFeatureFlags';
import { useAuth } from '../contexts/AuthContext';
import
{
    DomainFocusArea,
    DomainKnowledge,
    EnterpriseKnowledge,
    EnterpriseKnowledgePayload,
    KnowledgeDocument,
    ProjectWithRepository,
    UpdateProjectPayload,
} from '../types/project';
import { saveProjectDomainFocusAreas, uploadKnowledgeDocuments } from '../api/projects';
import apiClient, { API_ENDPOINTS } from '../api/config';
import { SYNTHETIC_DATA_DISABLED_MESSAGE, TEST_EXECUTION_DISABLED_MESSAGE } from '../constants/featureMessages';
import { formatApiError } from '../utils/apiError';
import './ProjectDetail-new.css';

/* ============================================================
   Type definitions
   ============================================================ */

/** Action feedback message shown as an alert */
interface ActionMessage
{
    type: 'success' | 'danger' | 'info';
    text: string;
}

/** Logs viewer modal state */
interface LogsModalState
{
    show: boolean;
    title: string;
    content: string;
    loading: boolean;
}

interface AdoValidationResult
{
    configured: boolean;
    test_plan_id?: number | null;
    test_suite_id?: number | null;
    test_configuration_id?: number | null;
    linked_test_cases: number;
    matched_test_points: number;
    missing_from_suite: Array<{ test_key?: string; ado_work_item_id?: number }>;
    permission_error?: string | null;
}

/** Tab identifiers */
type TabKey = 'domain' | 'requirements' | 'jobs' | 'test-cases' | 'gherkin' | 'executions' | 'synthetic-data-gen' | 'analytics';

/* ============================================================
   Helpers
   ============================================================ */

function normalizeDomainKnowledgeStatus(status?: string | null): string
{
    const lower = (status || '').trim().toLowerCase();

    if (lower === 'ready' || lower === 'success' || lower === 'succeeded')
    {
        return 'ready';
    }

    if (lower === 'complete')
    {
        return 'completed';
    }

    if (lower === 'processing' || lower === 'running' || lower === 'queued' || lower === 'updating')
    {
        return 'pending';
    }

    return lower;
}

function isTerminalDomainKnowledgeStatus(status: string): boolean
{
    return status === 'ready' || status === 'completed' || status === 'failed';
}

function isEnterpriseKnowledgeGenerationStatus(status?: string | null): boolean
{
    const lower = (status || '').trim().toLowerCase();
    return lower === 'processing' || lower === 'running' || lower === 'queued' || lower === 'updating';
}

function isTerminalEnterpriseKnowledgeStatus(status: string): boolean
{
    return status === 'ready' || status === 'completed' || status === 'failed';
}

function isNoEnterpriseDataMessage(message?: string | null): boolean
{
    return (message || '').toLowerCase().includes('no enterprise-specific content');
}

/**
 * The upload endpoint treats a file whose content (sha256) matches an
 * already-stored document as a no-op match rather than creating a new row --
 * intentional dedup, but a plain "uploaded successfully" message reads as
 * though a new file was added when it was not. Build a message that reflects
 * what actually happened.
 */
function describeKnowledgeUploadOutcome(uploaded: KnowledgeDocument[], successText: string): ActionMessage
{
    const duplicates = uploaded.filter((doc) => doc.is_duplicate);
    if (duplicates.length === 0)
    {
        return { type: 'success', text: successText };
    }

    const duplicateNames = duplicates.map((doc) => `"${doc.original_filename}"`).join(', ');
    const newCount = uploaded.length - duplicates.length;
    if (newCount === 0)
    {
        return {
            type: 'info',
            text: `No new document was added -- the uploaded content is identical to an existing document: ${duplicateNames}.`,
        };
    }

    return {
        type: 'info',
        text: `${successText} ${duplicates.length} file(s) matched content already on file and were not duplicated: ${duplicateNames}.`,
    };
}

/* ============================================================
   Component: ProjectDetailPageNew
   ============================================================ */

/**
 * ProjectDetailPageNew
 * Handles all data-fetching/mutation logic for the project detail view
 * and renders the redesigned UI including the "Repository Test Generation" tab.
 */
function ProjectDetailPageNew(): React.ReactElement
{
    /* ---------- Route params ---------- */
    const { projectId } = useParams<{ projectId?: string }>();
    const numericProjectId = useMemo(() => Number(projectId ?? NaN), [projectId]);

    /* ---------- Context ---------- */
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { flags: featureFlags, maintenance, loading: featureFlagsLoading } = useActiveFeatureFlags();
    const { user } = useAuth();
    const
    {
        projects,
        loading,
        error,
        refreshProject,
        updateProject,
        domainKnowledge: domainKnowledgeMap,
        enterpriseKnowledge: enterpriseKnowledgeMap,
        fetchDomainKnowledge,
        refreshDomainKnowledge,
        fetchEnterpriseKnowledge,
        refreshEnterpriseKnowledge,
        saveEnterpriseKnowledge
    } = useProjects();

    /* ---------- Derived project data ---------- */
    const project = useMemo(
        () => projects.find((p) => p.id === numericProjectId) || null,
        [projects, numericProjectId]
    ) as ProjectWithRepository | null;

    const domainKnowledgeEntry = useMemo<DomainKnowledge | null>(
        () => (Number.isFinite(numericProjectId) ? (domainKnowledgeMap[numericProjectId] ?? null) : null),
        [domainKnowledgeMap, numericProjectId]
    );

    const enterpriseKnowledgeEntry = useMemo<EnterpriseKnowledge | null>(
        () => (Number.isFinite(numericProjectId) ? (enterpriseKnowledgeMap[numericProjectId] ?? null) : null),
        [enterpriseKnowledgeMap, numericProjectId]
    );

    /* ---------- Local state ---------- */
    const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
    const [logsModal, setLogsModal] = useState<LogsModalState>(
        {
            show: false,
            title: '',
            content: '',
            loading: false
        }
    );
    const [domainKnowledgeLoading, setDomainKnowledgeLoading] = useState(false);
    const [domainKnowledgeRefreshing, setDomainKnowledgeRefreshing] = useState(false);
    const [domainFocusAreasSaving, setDomainFocusAreasSaving] = useState(false);
    const [knowledgeDocumentUploading, setKnowledgeDocumentUploading] = useState(false);
    const [awaitingBackgroundDomainKnowledge, setAwaitingBackgroundDomainKnowledge] = useState(false);
    const [enterpriseKnowledgeLoading, setEnterpriseKnowledgeLoading] = useState(false);
    const [enterpriseKnowledgeSaving, setEnterpriseKnowledgeSaving] = useState(false);
    const [enterpriseKnowledgeRefreshing, setEnterpriseKnowledgeRefreshing] = useState(false);
    const [enterpriseDocumentUploading, setEnterpriseDocumentUploading] = useState(false);
    const [awaitingBackgroundEnterpriseKnowledge, setAwaitingBackgroundEnterpriseKnowledge] = useState(false);

    /* Tab state — driven by ?tab= URL search param */
    const activeTab = useMemo<TabKey>(() =>
    {
        const t = searchParams.get('tab');
        if (t === 'domain' || t === 'requirements' || t === 'jobs' || t === 'test-cases' || t === 'gherkin' || t === 'executions' || t === 'synthetic-data-gen' || t === 'analytics') return t as TabKey;
        return 'test-cases';
    }, [searchParams]);
    const syntheticDataEnabled = !featureFlagsLoading && featureFlags.synthetic_data;
    const testExecutionEnabled = !featureFlagsLoading && featureFlags.test_execution;
    const isSuperAdmin = Boolean(user && /superadmin/i.test(user.role));
    const testGenerationMaintenance = maintenance.test_generation;
    const testGenerationMaintenanceActive = Boolean(
        !isSuperAdmin
        && featureFlags.testcase_generation
        && testGenerationMaintenance?.maintenance_enabled,
    );
    const testExecutionMaintenance = maintenance.playwright_execution;
    const testExecutionMaintenanceActive = Boolean(
        !isSuperAdmin
        && featureFlags.test_execution
        && testExecutionMaintenance?.maintenance_enabled,
    );

    const [selectedRequirementJobId, setSelectedRequirementJobId] = useState<number | null>(() =>
    {
        const jobIdParam = Number(searchParams.get('jobId') ?? NaN);
        return Number.isFinite(jobIdParam) ? jobIdParam : null;
    });
    const startedGherkinJobId = useMemo(() =>
    {
        const jobIdParam = Number(searchParams.get('gherkinJobId') ?? NaN);
        return Number.isFinite(jobIdParam) ? jobIdParam : null;
    }, [searchParams]);
    const selectedExecutionJobId = useMemo(() =>
    {
        if (activeTab !== 'executions') return null;
        const jobIdParam = Number(searchParams.get('jobId') ?? NaN);
        return Number.isFinite(jobIdParam) ? jobIdParam : null;
    }, [activeTab, searchParams]);
    const selectedJobsTabJobId = useMemo(() =>
    {
        if (activeTab !== 'jobs') return null;
        const jobIdParam = Number(searchParams.get('jobId') ?? NaN);
        return Number.isFinite(jobIdParam) ? jobIdParam : null;
    }, [activeTab, searchParams]);
    const shouldTrackGherkinStatus = searchParams.get('trackGherkin') === '1' || startedGherkinJobId !== null;

    /* Azure DevOps configuration modal state */
    const [azureConfigModalOpen, setAzureConfigModalOpen] = useState(false);
    const [azureOrgValue, setAzureOrgValue] = useState('');
    const [azureProjectValue, setAzureProjectValue] = useState('');
    const [azureTeamValue, setAzureTeamValue] = useState('');
    const [azurePatValue, setAzurePatValue] = useState('');
    const [azureConfigError, setAzureConfigError] = useState<string | null>(null);
    const [azureConfigSubmitting, setAzureConfigSubmitting] = useState(false);
    const [adoPlanValidating, setAdoPlanValidating] = useState(false);
    const [adoValidation, setAdoValidation] = useState<AdoValidationResult | null>(null);

    /* ---------- Derived flags ---------- */
    const isAzureProject = (project?.project_type ?? '').toLowerCase() === 'azure_devops';
    const azureInfo = project?.azure_devops ?? null;
    const managedAdoPlanName = project?.name ? `AcceliQA - ${project.name}` : 'AcceliQA managed test plan';
    const effectiveAdoTestPlanId = azureInfo?.test_plan_id
        ?? (adoValidation?.configured ? (adoValidation.test_plan_id ?? null) : null);
    const effectiveAdoTestSuiteId = azureInfo?.test_suite_id
        ?? (adoValidation?.configured ? (adoValidation.test_suite_id ?? null) : null);
    const effectiveAdoTestConfigurationId = azureInfo?.test_configuration_id
        ?? (adoValidation?.configured ? (adoValidation.test_configuration_id ?? null) : null);
    const actionsDisabled = loading;
    const effectiveDomainKnowledgeStatus = useMemo(
        () => normalizeDomainKnowledgeStatus(domainKnowledgeEntry?.status ?? project?.domain_knowledge_status ?? null),
        [domainKnowledgeEntry?.status, project?.domain_knowledge_status]
    );
    const effectiveEnterpriseKnowledgeStatus = useMemo(
        () => normalizeDomainKnowledgeStatus(enterpriseKnowledgeEntry?.status ?? null),
        [enterpriseKnowledgeEntry?.status]
    );
    const enterpriseGenerationActive = awaitingBackgroundEnterpriseKnowledge
        || enterpriseKnowledgeRefreshing
        || isEnterpriseKnowledgeGenerationStatus(enterpriseKnowledgeEntry?.status);

    useEffect(
        function syncAdoTestPlanFields()
        {
            setAdoValidation(null);
        },
        [azureInfo?.test_plan_id]
    );

    useEffect(
        function resetShellStateOnProjectChange()
        {
            setSelectedRequirementJobId(null);
            setAwaitingBackgroundDomainKnowledge(false);
            setAwaitingBackgroundEnterpriseKnowledge(false);
        },
        [numericProjectId]
    );

    useEffect(
        function syncSelectedRequirementJobFromUrl()
        {
            if (activeTab !== 'test-cases')
            {
                setSelectedRequirementJobId(null);
                return;
            }
            const jobIdParam = Number(searchParams.get('jobId') ?? NaN);
            if (Number.isFinite(jobIdParam))
            {
                setSelectedRequirementJobId(jobIdParam);
                return;
            }
            setSelectedRequirementJobId(null);
        },
        [activeTab, searchParams]
    );

    useEffect(
        function trackExpectedBackgroundGeneration()
        {
            if (domainKnowledgeEntry && isTerminalDomainKnowledgeStatus(effectiveDomainKnowledgeStatus))
            {
                setAwaitingBackgroundDomainKnowledge(false);
                return;
            }

            if (!domainKnowledgeEntry && Boolean((project?.domain_description || '').trim()))
            {
                setAwaitingBackgroundDomainKnowledge(true);
            }
        },
        [domainKnowledgeEntry, effectiveDomainKnowledgeStatus, project?.domain_description]
    );

    useEffect(
        function trackExpectedBackgroundEnterpriseGeneration()
        {
            if (enterpriseKnowledgeEntry && isTerminalEnterpriseKnowledgeStatus(effectiveEnterpriseKnowledgeStatus))
            {
                setAwaitingBackgroundEnterpriseKnowledge(false);
            }
        },
        [enterpriseKnowledgeEntry, effectiveEnterpriseKnowledgeStatus]
    );

    /* ---------- Data loading ---------- */

    /** Load the latest project data from context */
    const loadProject = useCallback(
        async function loadProjectCallback(): Promise<void>
        {
            if (!Number.isFinite(numericProjectId))
            {
                return;
            }

            try
            {
                await refreshProject(numericProjectId);
            }
            catch
            {
                /* errors surfaced via context error state */
            }
        },
        [numericProjectId, refreshProject]
    );

    /** Load domain knowledge for the current project */
    const loadDomainKnowledge = useCallback(
        async function loadDomainKnowledgeCallback(): Promise<void>
        {
            if (!Number.isFinite(numericProjectId))
            {
                return;
            }

            setDomainKnowledgeLoading(true);

            try
            {
                await fetchDomainKnowledge(numericProjectId);
            }
            catch
            {
                /* errors handled via context state */
            }
            finally
            {
                setDomainKnowledgeLoading(false);
            }
        },
        [fetchDomainKnowledge, numericProjectId]
    );

    const loadEnterpriseKnowledge = useCallback(
        async function loadEnterpriseKnowledgeCallback(): Promise<void>
        {
            if (!Number.isFinite(numericProjectId))
            {
                return;
            }

            setEnterpriseKnowledgeLoading(true);

            try
            {
                await fetchEnterpriseKnowledge(numericProjectId);
            }
            catch
            {
                /* errors handled via context state */
            }
            finally
            {
                setEnterpriseKnowledgeLoading(false);
            }
        },
        [fetchEnterpriseKnowledge, numericProjectId]
    );

    useEffect(
        function initialLoad()
        {
            loadProject();
        },
        [loadProject]
    );

    useEffect(
        function reloadKnowledgeWhenTabOpens()
        {
            if (activeTab !== 'domain')
            {
                return;
            }

            void loadDomainKnowledge();
            void loadEnterpriseKnowledge();
        },
        [activeTab, loadDomainKnowledge, loadEnterpriseKnowledge]
    );

    useEffect(
        function pollForBackgroundDomainKnowledge()
        {
            if (activeTab !== 'domain' || !Number.isFinite(numericProjectId))
            {
                return;
            }

            const shouldPoll = awaitingBackgroundDomainKnowledge
                || effectiveDomainKnowledgeStatus === 'pending';

            if (!shouldPoll || isTerminalDomainKnowledgeStatus(effectiveDomainKnowledgeStatus))
            {
                return;
            }

            let cancelled = false;
            let attempts = 0;

            async function pollOnce(): Promise<boolean>
            {
                try
                {
                    const nextKnowledge = await fetchDomainKnowledge(numericProjectId);
                    const nextStatus = normalizeDomainKnowledgeStatus(nextKnowledge?.status ?? null);

                    if (nextKnowledge && isTerminalDomainKnowledgeStatus(nextStatus))
                    {
                        if (!cancelled)
                        {
                            setAwaitingBackgroundDomainKnowledge(false);
                        }
                        return true;
                    }
                }
                catch
                {
                }

                return false;
            }

            const interval = window.setInterval(() =>
            {
                attempts += 1;
                void pollOnce().then((done) =>
                {
                    if (done || attempts >= 10)
                    {
                        if (!done && !cancelled)
                        {
                            setAwaitingBackgroundDomainKnowledge(false);
                        }
                        window.clearInterval(interval);
                    }
                });
            }, 3000);

            void pollOnce();

            return function cleanup(): void
            {
                cancelled = true;
                window.clearInterval(interval);
            };
        },
        [
            activeTab,
            awaitingBackgroundDomainKnowledge,
            effectiveDomainKnowledgeStatus,
            fetchDomainKnowledge,
            numericProjectId,
        ]
    );

    useEffect(
        function pollForBackgroundEnterpriseKnowledge()
        {
            if (activeTab !== 'domain' || !Number.isFinite(numericProjectId))
            {
                return;
            }

            const shouldPoll = awaitingBackgroundEnterpriseKnowledge
                || isEnterpriseKnowledgeGenerationStatus(enterpriseKnowledgeEntry?.status);

            if (!shouldPoll || isTerminalEnterpriseKnowledgeStatus(effectiveEnterpriseKnowledgeStatus))
            {
                return;
            }

            let cancelled = false;
            let attempts = 0;

            async function pollOnce(): Promise<boolean>
            {
                try
                {
                    const nextKnowledge = await fetchEnterpriseKnowledge(numericProjectId);
                    const nextStatus = normalizeDomainKnowledgeStatus(nextKnowledge?.status ?? null);

                    if (nextKnowledge && isTerminalEnterpriseKnowledgeStatus(nextStatus))
                    {
                        if (!cancelled)
                        {
                            setAwaitingBackgroundEnterpriseKnowledge(false);
                        }
                        return true;
                    }
                }
                catch
                {
                }

                return false;
            }

            const interval = window.setInterval(() =>
            {
                attempts += 1;
                void pollOnce().then((done) =>
                {
                    if (done || attempts >= 20)
                    {
                        if (!done)
                        {
                            setAwaitingBackgroundEnterpriseKnowledge(false);
                        }
                        window.clearInterval(interval);
                    }
                });
            }, 3000);

            void pollOnce();

            return function cleanup(): void
            {
                cancelled = true;
                window.clearInterval(interval);
            };
        },
        [
            activeTab,
            awaitingBackgroundEnterpriseKnowledge,
            effectiveEnterpriseKnowledgeStatus,
            enterpriseKnowledgeEntry?.status,
            fetchEnterpriseKnowledge,
            numericProjectId,
        ]
    );

    /* ---------- Event handlers ---------- */

    /** Navigate back to the projects list */
    function handleBackToProjects(): void
    {
        navigate('/projects');
    }

    /** Switch the active tab — updates URL search param */
    function handleSelectTab(tab: TabKey): void
    {
        setSearchParams({ tab });
    }

    function handleOpenJobInTestCases(jobId: number): void
    {
        setSelectedRequirementJobId(jobId);
        setSearchParams({ tab: 'test-cases', jobId: String(jobId) });
    }

    /** Close the logs modal */
    function handleCloseLogsModal(): void
    {
        setLogsModal((prev) =>
        {
            return { ...prev, show: false };
        });
    }

    /** Refresh the domain knowledge model for this project */
    async function handleRefreshDomainKnowledge(documentIds?: number[]): Promise<void>
    {
        if (!Number.isFinite(numericProjectId))
        {
            return;
        }

        setDomainKnowledgeRefreshing(true);

        try
        {
            await refreshDomainKnowledge(numericProjectId, { documentIds: documentIds ?? [] });
            await refreshProject(numericProjectId);
            setActionMessage({ type: 'success', text: 'Domain knowledge updated.' });
        }
        catch (err: unknown)
        {
            const apiError = err as { response?: { data?: { detail?: string } }; message?: string };
            const message = apiError?.response?.data?.detail || apiError?.message || 'Failed to refresh domain knowledge';
            setActionMessage({ type: 'danger', text: message });
        }
        finally
        {
            setDomainKnowledgeRefreshing(false);
        }
    }

    async function handleSaveDomainFocusAreas(focusAreas: DomainFocusArea[]): Promise<void>
    {
        if (!Number.isFinite(numericProjectId))
        {
            return;
        }

        setDomainFocusAreasSaving(true);

        try
        {
            await saveProjectDomainFocusAreas(numericProjectId, focusAreas);
            await loadDomainKnowledge();
            setActionMessage({ type: 'success', text: 'Testing focus areas saved.' });
        }
        catch (err: unknown)
        {
            const apiError = err as { response?: { data?: { detail?: string } }; message?: string };
            const message = apiError?.response?.data?.detail || apiError?.message || 'Failed to save testing focus areas';
            setActionMessage({ type: 'danger', text: message });
            throw err;
        }
        finally
        {
            setDomainFocusAreasSaving(false);
        }
    }

    async function handleUploadDomainKnowledgeDocuments(files: File[]): Promise<void>
    {
        if (!Number.isFinite(numericProjectId) || files.length === 0)
        {
            return;
        }

        setKnowledgeDocumentUploading(true);

        try
        {
            const uploaded = await uploadKnowledgeDocuments(numericProjectId, files, 'domain');
            setAwaitingBackgroundDomainKnowledge(true);
            await loadDomainKnowledge();
            setActionMessage(describeKnowledgeUploadOutcome(uploaded, 'Knowledge documents uploaded.'));
        }
        catch (err: unknown)
        {
            const message = formatApiError(err, 'Failed to upload knowledge documents');
            setActionMessage({ type: 'danger', text: message });
            throw err;
        }
        finally
        {
            setKnowledgeDocumentUploading(false);
        }
    }

    async function handleSaveEnterpriseKnowledge(payload: EnterpriseKnowledgePayload): Promise<void>
    {
        if (!Number.isFinite(numericProjectId))
        {
            return;
        }

        setEnterpriseKnowledgeSaving(true);

        try
        {
            await saveEnterpriseKnowledge(numericProjectId, payload);
            await refreshProject(numericProjectId);
            setActionMessage({ type: 'success', text: 'Enterprise knowledge saved.' });
        }
        catch (err: unknown)
        {
            const apiError = err as { response?: { data?: { detail?: string } }; message?: string };
            const message = apiError?.response?.data?.detail || apiError?.message || 'Failed to save enterprise knowledge';
            setActionMessage({ type: 'danger', text: message });
            throw err;
        }
        finally
        {
            setEnterpriseKnowledgeSaving(false);
        }
    }

    /** Refresh AI-generated enterprise knowledge */
    async function handleRefreshEnterpriseKnowledge(documentIds?: number[]): Promise<void>
    {
        if (!Number.isFinite(numericProjectId))
        {
            return;
        }

        setEnterpriseKnowledgeRefreshing(true);

        try
        {
            await refreshEnterpriseKnowledge(numericProjectId, { documentIds: documentIds ?? [] });
            await refreshProject(numericProjectId);
            setActionMessage({ type: 'success', text: 'Enterprise knowledge refreshed.' });
        }
        catch (err: unknown)
        {
            const apiError = err as { response?: { data?: { detail?: string } }; message?: string };
            const message = apiError?.response?.data?.detail || apiError?.message || 'Failed to refresh enterprise knowledge';
            if (isNoEnterpriseDataMessage(message))
            {
                await loadEnterpriseKnowledge();
                setActionMessage({
                    type: 'info',
                    text: 'No enterprise-specific knowledge was found in the uploaded documents. You can add it manually.',
                });
            }
            else
            {
                setActionMessage({ type: 'danger', text: message });
            }
        }
        finally
        {
            setEnterpriseKnowledgeRefreshing(false);
        }
    }

    /** Upload enterprise knowledge documents */
    async function handleUploadEnterpriseKnowledgeDocuments(files: File[]): Promise<void>
    {
        if (!Number.isFinite(numericProjectId))
        {
            return;
        }

        setEnterpriseDocumentUploading(true);

        try
        {
            const uploaded = await uploadKnowledgeDocuments(numericProjectId, files, 'enterprise');
            setAwaitingBackgroundEnterpriseKnowledge(true);
            await loadEnterpriseKnowledge();
            setActionMessage(describeKnowledgeUploadOutcome(uploaded, 'Enterprise documents uploaded. Generating enterprise knowledge.'));
        }
        catch (err: unknown)
        {
            const message = formatApiError(err, 'Failed to upload enterprise documents');
            setActionMessage({ type: 'danger', text: message });
            throw err;
        }
        finally
        {
            setEnterpriseDocumentUploading(false);
        }
    }

    /* ---------- Azure DevOps configuration handlers ---------- */

    /** Open the Azure DevOps configuration modal */
    function handleOpenAzureConfig(): void
    {
        setAzureOrgValue(azureInfo?.organization || azureInfo?.tenant || '');
        setAzureProjectValue(azureInfo?.project_name || '');
        setAzureTeamValue(azureInfo?.team_name || '');
        setAzurePatValue('');
        setAzureConfigError(null);
        setAzureConfigModalOpen(true);
    }

    /** Close the Azure DevOps configuration modal */
    function handleCloseAzureConfig(): void
    {
        if (azureConfigSubmitting)
        {
            return;
        }

        setAzureConfigModalOpen(false);
        setAzureOrgValue('');
        setAzureProjectValue('');
        setAzureTeamValue('');
        setAzurePatValue('');
        setAzureConfigError(null);
    }

    /** Submit Azure DevOps configuration changes */
    async function handleSubmitAzureConfig(): Promise<void>
    {
        if (!project)
        {
            return;
        }

        const trimmedOrg = azureOrgValue.trim();
        const trimmedProject = azureProjectValue.trim();
        const trimmedTeam = azureTeamValue.trim();
        const trimmedPat = azurePatValue.trim();

        if (!trimmedOrg || !trimmedProject || !trimmedTeam)
        {
            setAzureConfigError('Organization, project, and team are required.');
            return;
        }

        if (!azureInfo?.pat_configured && !trimmedPat)
        {
            setAzureConfigError('Personal access token is required.');
            return;
        }

        setAzureConfigSubmitting(true);
        setAzureConfigError(null);

        try
        {
            const payload: UpdateProjectPayload = {
                azure_devops_org: trimmedOrg,
                azure_devops_project: trimmedProject,
                azure_devops_team: trimmedTeam,
            };
            if (trimmedPat)
            {
                payload.azure_devops_pat = trimmedPat;
            }

            await updateProject(project.id, payload);
            setAzureConfigModalOpen(false);
            setAzurePatValue('');
            setActionMessage({ type: 'success', text: 'Azure DevOps configuration updated.' });
            await loadProject();
        }
        catch (err: unknown)
        {
            const apiError = err as { response?: { data?: { detail?: string } }; message?: string };
            const message = apiError?.response?.data?.detail || apiError?.message || 'Failed to update Azure DevOps configuration';
            setAzureConfigError(message);
        }
        finally
        {
            setAzureConfigSubmitting(false);
        }
    }

    async function handleValidateAdoTestPlan(): Promise<void>
    {
        if (!project)
        {
            return;
        }

        setAdoPlanValidating(true);
        setAdoValidation(null);
        try
        {
            const response = await apiClient.get<AdoValidationResult>(
                API_ENDPOINTS.PLAYWRIGHT_ADO_VALIDATE(project.id)
            );
            setAdoValidation(response.data);
            if (response.data?.configured && response.data?.test_plan_id)
            {
                await loadProject();
            }
        }
        catch (err: unknown)
        {
            const apiError = err as { response?: { data?: { detail?: string } }; message?: string };
            const message = apiError?.response?.data?.detail || apiError?.message || 'Failed to validate Azure Test Plans mapping';
            setActionMessage({ type: 'danger', text: message });
        }
        finally
        {
            setAdoPlanValidating(false);
        }
    }

    /* ============================================================
       Render helpers
       ============================================================ */

    /** Render the breadcrumb navigation row */
    function renderBreadcrumb(): React.ReactNode
    {
        return (
            <div className="pd-breadcrumb-row">
                <nav className="pd-breadcrumb" aria-label="Breadcrumb">
                    <button
                        className="pd-breadcrumb-link"
                        type="button"
                        onClick={handleBackToProjects}
                    >
                        Projects
                    </button>
                    <ChevronRight size={12} className="pd-breadcrumb-sep" />
                    <span className="pd-breadcrumb-current">
                        {project?.name ?? 'Project Detail'}
                    </span>
                </nav>

                <div className="pd-breadcrumb-actions">
                </div>
            </div>
        );
    }

    /** Render the project header card with name, badges, description */
    function renderProjectHeader(): React.ReactNode
    {
        if (!project)
        {
            return null;
        }

        const projectDomainDescription = (project.domain_description || '').trim() || 'No business or application context provided yet.';

        return (
            <div className="pd-header-card">
                {/* Title row */}
                <div className="pd-header-row">
                    {/* Left: project name */}
                    <div className="pd-header-left">
                        <h1 className="pd-project-name">
                            <FolderFill className="pd-project-icon" size={22} />
                            {project.name}
                        </h1>
                    </div>

                    {/* Right: badges + quick-action buttons */}
                    <div className="pd-header-right">
                        {/* Status + project-type badges */}
                        <div className="pd-badge-row">
                            <span className={`pd-badge ${isAzureProject ? 'pd-badge--azure' : 'pd-badge--secondary'}`}>
                                {isAzureProject ? 'Azure DevOps' : 'Simple'}
                            </span>
                        </div>

                        {/* Quick-action buttons (view repository, results navigation) */}
                        <div className="pd-header-actions">
                            <button
                                className="pd-header-btn pd-header-btn--primary"
                                type="button"
                                onClick={() => handleSelectTab('test-cases')}
                            >
                                Open Test Cases
                            </button>

                            <button
                                className="pd-header-btn"
                                type="button"
                                onClick={() => handleSelectTab('requirements')}
                            >
                                Requirements Workspace
                            </button>

                        </div>
                    </div>
                </div>

                <div className="pd-project-description-block">
                    <span className="pd-project-description-label">Business and Application Context</span>
                    <p className="pd-project-description">{projectDomainDescription}</p>
                </div>

                {/* Azure DevOps info section */}
                {isAzureProject && azureInfo && (
                    <div className="pd-azure-info">
                        <div style={{ flex: 1 }}>
                            <p className="pd-azure-info-title">Azure DevOps Integration</p>
                            <p className="pd-azure-info-detail">
                                Organization: {azureInfo.organization}
                            </p>
                            <p className="pd-azure-info-detail">
                                Project: {azureInfo.project_name}
                            </p>
                            <p className="pd-azure-info-detail">
                                Team: {azureInfo.team_name}
                            </p>
                            <p className="pd-azure-info-detail">
                                PAT: {azureInfo.pat_configured ? 'Configured ✓' : 'Not configured'}
                            </p>
                            <div className="pd-ado-plan-status">
                                {effectiveAdoTestPlanId ? (
                                    <>
                                        <p><strong>Azure Test Plan:</strong> {managedAdoPlanName}</p>
                                        <p><strong>Plan ID:</strong> {effectiveAdoTestPlanId}</p>
                                        {effectiveAdoTestSuiteId && (
                                            <p><strong>Suite ID:</strong> {effectiveAdoTestSuiteId}</p>
                                        )}
                                        {effectiveAdoTestConfigurationId && (
                                            <p><strong>Configuration ID:</strong> {effectiveAdoTestConfigurationId}</p>
                                        )}
                                        <p>Managed by AcceliQA</p>
                                    </>
                                ) : (
                                    <p>No Azure Test Plan mapped yet. Push test cases to ADO to create one automatically.</p>
                                )}
                            </div>
                            {adoValidation && (
                                <div className="pd-ado-validation">
                                    {!adoValidation.configured ? (
                                        <span>No Azure Test Plan is mapped yet. Push test cases to ADO to create it automatically.</span>
                                    ) : adoValidation.permission_error ? (
                                        <span>{adoValidation.permission_error}</span>
                                    ) : (
                                        <span>
                                            {adoValidation.matched_test_points} of {adoValidation.linked_test_cases} linked test cases found in mapped suites.
                                            {adoValidation.missing_from_suite.length > 0
                                                ? ` ${adoValidation.missing_from_suite.length} missing from mapped suites.`
                                                : ' Mapping looks ready.'}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="pd-azure-actions">
                            <button
                                className="pd-header-btn"
                                type="button"
                                onClick={handleValidateAdoTestPlan}
                                disabled={adoPlanValidating || actionsDisabled}
                            >
                                {adoPlanValidating ? 'Validating...' : 'Validate Mapping'}
                            </button>
                            <button
                                className="pd-header-btn"
                                type="button"
                                onClick={handleOpenAzureConfig}
                                disabled={azureConfigSubmitting || actionsDisabled}
                            >
                                Edit Config
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    /** Render the custom tab navigation bar */
    function renderTabBar(): React.ReactNode
    {
        const tabs: Array<{
            key: TabKey;
            label: string;
            helper: string;
        }> = [
            { key: 'domain', label: 'Domain & Enterprise Knowledge', helper: 'Business context and enterprise rules' },
            {
                key: 'requirements',
                label: 'Requirements Workspace',
                helper: 'Upload documents, sync ADO, and generate tests',
            },
            { key: 'test-cases', label: 'Test Cases', helper: 'Browse previously generated tests and results' },
            { key: 'gherkin', label: 'Gherkin / BDD', helper: 'Generate & download .feature files' },
            { key: 'executions', label: 'Executions', helper: 'Playwright run history and results' },
            { key: 'synthetic-data-gen', label: 'Synthetic Data Generation', helper: 'Generate test data variants from test cases' },
            { key: 'analytics', label: 'Analytics', helper: 'Coverage, jobs, documents, and sync health' },
            { key: 'jobs', label: 'Jobs', helper: 'Monitor all generation and execution jobs' },
        ];

        const visibleTabs = tabs.filter((tab) =>
        {
            if (tab.key === 'executions')
            {
                return featureFlagsLoading || featureFlags.test_execution;
            }
            return true;
        });

        return (
            <div className="pd-tab-bar">
                {visibleTabs.map((tab) =>
                {
                    const isActive = activeTab === tab.key;

                    return (
                        <button
                            key={tab.key}
                            className={`pd-tab-btn ${isActive ? 'pd-tab-btn--active' : ''}`}
                            type="button"
                            onClick={() => handleSelectTab(tab.key)}
                        >
                            <span className="pd-tab-btn-copy">
                                {tab.label}
                            </span>
                            <span className="pd-tab-btn-helper">{tab.helper}</span>
                        </button>
                    );
                })}
            </div>
        );
    }

    /** Render the full tab panel based on active tab key */
    function renderTabContent(): React.ReactNode
    {
        if (activeTab === 'test-cases')
        {
            return (
                <RequirementTestWorkspace
                    projectId={numericProjectId}
                    projectName={project?.name}
                    selectedJobId={selectedRequirementJobId}
                    onSelectedJobIdChange={setSelectedRequirementJobId}
                    // Keep the generation selector visible so older snapshots
                    // (including Version 1) remain directly inspectable.
                    showJobSelector={true}
                    variant="embedded"
                    initialTestKey={searchParams.get('testKey') || null}
                />
            );
        }

        if (activeTab === 'domain')
        {
            return (
                <DomainKnowledgePanel
                    project={project as ProjectWithRepository}
                    knowledge={domainKnowledgeEntry}
                    enterpriseKnowledge={enterpriseKnowledgeEntry}
                    status={domainKnowledgeEntry?.status ?? project?.domain_knowledge_status}
                    lastRefreshedAt={domainKnowledgeEntry?.last_refreshed_at ?? project?.domain_knowledge_last_refreshed_at ?? null}
                    loading={domainKnowledgeLoading}
                    refreshing={domainKnowledgeRefreshing}
                    focusAreasSaving={domainFocusAreasSaving}
                    uploadingDocuments={knowledgeDocumentUploading}
                    enterpriseLoading={enterpriseKnowledgeLoading}
                    enterpriseSaving={enterpriseKnowledgeSaving}
                    enterpriseRefreshing={enterpriseKnowledgeRefreshing}
                    enterpriseGenerating={enterpriseGenerationActive}
                    uploadingEnterpriseDocuments={enterpriseDocumentUploading}
                    onRefresh={handleRefreshDomainKnowledge}
                    onSaveFocusAreas={handleSaveDomainFocusAreas}
                    onUploadKnowledgeDocuments={handleUploadDomainKnowledgeDocuments}
                    onSaveEnterpriseKnowledge={handleSaveEnterpriseKnowledge}
                    onRefreshEnterprise={handleRefreshEnterpriseKnowledge}
                    onUploadEnterpriseKnowledgeDocuments={handleUploadEnterpriseKnowledgeDocuments}
                />
            );
        }

        if (activeTab === 'requirements')
        {
            if (featureFlagsLoading)
            {
                return (
                    <div className="d-flex justify-content-center align-items-center gap-2 py-5">
                        <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                        <span className="text-muted">Loading...</span>
                    </div>
                );
            }
            if (testGenerationMaintenanceActive)
            {
                return (
                    <MaintenancePage
                        contextLabel="Requirements workspace"
                        serviceName={testGenerationMaintenance?.service_name || 'Test Generation'}
                        message={testGenerationMaintenance?.message}
                    />
                );
            }
            return (
                <ProjectRequirementsPanelNew
                    projectId={numericProjectId}
                    onSelectJobId={handleOpenJobInTestCases}
                />
            );
        }

        if (activeTab === 'jobs')
        {
            return (
                <TestGenerations
                    projectId={numericProjectId}
                    projectName={project?.name}
                    initialJobId={selectedJobsTabJobId}
                />
            );
        }

        if (activeTab === 'executions')
        {
            if (featureFlagsLoading)
            {
                return (
                    <div className="d-flex justify-content-center align-items-center gap-2 py-5">
                        <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                        <span className="text-muted">Loading...</span>
                    </div>
                );
            }

            if (!testExecutionEnabled)
            {
                return (
                    <Alert variant="warning" className="m-3">
                        {TEST_EXECUTION_DISABLED_MESSAGE}
                    </Alert>
                );
            }

            if (testExecutionMaintenanceActive)
            {
                return (
                    <MaintenancePage
                        contextLabel="Executions"
                        serviceName={testExecutionMaintenance?.service_name || 'Playwright Execution'}
                        message={testExecutionMaintenance?.message}
                    />
                );
            }

            return (
                <ExecutionsTab projectId={numericProjectId} initialJobId={selectedExecutionJobId} />
            );
        }

        if (activeTab === 'synthetic-data-gen')
        {
            if (featureFlagsLoading)
            {
                return (
                    <div className="d-flex justify-content-center align-items-center gap-2 py-5">
                        <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                        <span className="text-muted">Loading...</span>
                    </div>
                );
            }

            if (!syntheticDataEnabled)
            {
                return (
                    <Alert variant="warning" className="m-3">
                        {SYNTHETIC_DATA_DISABLED_MESSAGE}
                    </Alert>
                );
            }

            return (
                <SyntheticDataTab projectId={numericProjectId} />
            );
        }

        if (activeTab === 'analytics')
        {
            return (
                <ProjectAnalyticsTab
                    projectId={numericProjectId}
                    projectName={project?.name}
                />
            );
        }

        return null;
    }

    /** Render the Azure DevOps configuration modal */
    function renderAzureConfigModal(): React.ReactNode
    {
        return (
            <Modal
                show={azureConfigModalOpen}
                onHide={handleCloseAzureConfig}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>
                        Edit Azure DevOps Configuration
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <label className="pd-modal-label" htmlFor="pd-azure-org-input">
                        Organization
                    </label>
                    <input
                        id="pd-azure-org-input"
                        className="pd-modal-input"
                        type="text"
                        placeholder="my-organization"
                        value={azureOrgValue}
                        onChange={(event) => setAzureOrgValue(event.target.value)}
                        disabled={azureConfigSubmitting}
                    />

                    <label className="pd-modal-label pd-modal-label--spaced" htmlFor="pd-azure-project-input">
                        Project
                    </label>
                    <input
                        id="pd-azure-project-input"
                        className="pd-modal-input"
                        type="text"
                        placeholder="MyProject"
                        value={azureProjectValue}
                        onChange={(event) => setAzureProjectValue(event.target.value)}
                        disabled={azureConfigSubmitting}
                    />

                    <label className="pd-modal-label pd-modal-label--spaced" htmlFor="pd-azure-team-input">
                        Team
                    </label>
                    <input
                        id="pd-azure-team-input"
                        className="pd-modal-input"
                        type="text"
                        placeholder="MyTeam"
                        value={azureTeamValue}
                        onChange={(event) => setAzureTeamValue(event.target.value)}
                        disabled={azureConfigSubmitting}
                    />

                    <label className="pd-modal-label pd-modal-label--spaced" htmlFor="pd-azure-pat-input">
                        Personal Access Token
                    </label>
                    <input
                        id="pd-azure-pat-input"
                        className="pd-modal-input"
                        type="password"
                        placeholder="Leave blank to keep current PAT"
                        value={azurePatValue}
                        onChange={(event) => setAzurePatValue(event.target.value)}
                        disabled={azureConfigSubmitting}
                    />
                    <p className="pd-modal-help">
                        Enter a PAT only when rotating credentials or fixing a missing token.
                    </p>

                    {/* Inline error */}
                    {azureConfigError !== null && (
                        <Alert variant="danger" className="mt-3 mb-0 py-2" style={{ fontSize: '0.875rem' }}>
                            {azureConfigError}
                        </Alert>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    {/* Cancel */}
                    <button
                        className="pd-btn-secondary"
                        type="button"
                        onClick={handleCloseAzureConfig}
                        disabled={azureConfigSubmitting}
                    >
                        Cancel
                    </button>

                    {/* Confirm */}
                    <button
                        className="pd-btn-primary"
                        type="button"
                        onClick={handleSubmitAzureConfig}
                        disabled={azureConfigSubmitting}
                    >
                        {azureConfigSubmitting
                            ? (
                                <>
                                    <Spinner animation="border" size="sm" className="pd-btn-spinner" />
                                    Saving...
                                </>
                            )
                            : 'Save Configuration'
                        }
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }

    /** Render the logs viewer modal */
    function renderLogsModal(): React.ReactNode
    {
        return (
            <Modal
                show={logsModal.show}
                onHide={handleCloseLogsModal}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                        {logsModal.title}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body
                    style={
                        {
                            maxHeight: '60vh',
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem'
                        }
                    }
                >
                    {logsModal.loading
                        ? <Spinner animation="border" size="sm" />
                        : logsModal.content
                    }
                </Modal.Body>

                <Modal.Footer>
                    <button className="pd-btn-secondary" type="button" onClick={handleCloseLogsModal}>
                        Close
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }

    /* ---------- Guard renders ---------- */

    if (!Number.isFinite(numericProjectId))
    {
        return (
            <div className="pd-page">
                <Alert variant="danger">Invalid project ID.</Alert>
            </div>
        );
    }

    if (loading && !project)
    {
        return (
            <div className="pd-loading">
                <Spinner animation="border" />
            </div>
        );
    }

    if (!project)
    {
        return (
            <div className="pd-page">
                <Alert variant="warning">
                    Project not found.
                    <div className="mt-2">
                        <button
                            className="pd-btn-secondary"
                            type="button"
                            onClick={handleBackToProjects}
                        >
                            Back to Projects
                        </button>
                    </div>
                </Alert>
            </div>
        );
    }

    /* ---------- Main render ---------- */

    return (
        <div className="pd-page">
            {/* Breadcrumb */}
            {renderBreadcrumb()}

            {/* Project header card */}
            {renderProjectHeader()}

            {/* Context-level error */}
            {error && (
                <Alert variant="danger" className="pd-alert">{error}</Alert>
            )}

            {/* Action feedback message */}
            {actionMessage !== null && (
                <Alert
                    variant={actionMessage.type}
                    className="pd-alert"
                    onClose={() => setActionMessage(null)}
                    dismissible
                >
                    {actionMessage.text}
                </Alert>
            )}

            {/* Tabs card */}
            <div className="pd-tabs-card">
                {/* Tab navigation bar */}
                {renderTabBar()}

                {/* Active tab content */}
                <div className="pd-tab-content" role="tabpanel">
                    {renderTabContent()}
                    {/* GherkinTab is always mounted to preserve state; hidden when not active */}
                    <div style={{ display: activeTab === 'gherkin' ? undefined : 'none' }}>
                        <GherkinTab
                            projectId={numericProjectId}
                            selectedJobId={selectedRequirementJobId}
                            startedJobId={startedGherkinJobId}
                            trackStatus={shouldTrackGherkinStatus}
                            isActive={activeTab === 'gherkin'}
                        />
                    </div>
                </div>
            </div>

            {/* Modals */}
            {renderAzureConfigModal()}
            {renderLogsModal()}

            {Number.isFinite(numericProjectId) && (
                <ProjectChatPanel projectId={numericProjectId} />
            )}
        </div>
    );
}

/* ---------- Export ---------- */

export default ProjectDetailPageNew;
